import { QueryClient } from '@tanstack/vue-query';

/** Anything the caller cannot fix by retrying. */
function isFatal(status: number): boolean {
  if (status === 408 || status === 429) return false;   // worth another go
  return status >= 400 && status < 500;
}

function statusOf(error: unknown): number {
  return typeof error === 'object' && error !== null && 'status' in error
    ? Number((error as { status: unknown }).status)
    : 0;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * Zero, deliberately. Every list on this dashboard is observed state the
       * reconciler is actively changing underneath the operator — a cached
       * "healthy" from 30 seconds ago is a lie. Screens set their own
       * refetchInterval from lib/polling.ts.
       */
      staleTime: 0,
      gcTime: 5 * 60_000,

      /**
       * Off. The design system's rule is that a stale number keeps showing
       * with its age rather than flickering; refetching on every window focus
       * fights the explicit poll interval and makes the table jump.
       */
      refetchOnWindowFocus: false,

      /** Polls pause with the tab, and resume on return. */
      refetchIntervalInBackground: false,

      retry: (attempt: number, error: unknown) => !isFatal(statusOf(error)) && attempt < 3,
      retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 10_000),
    },

    mutations: {
      /**
       * Never. Every write here is a row change the reconciler acts on, and
       * several are not idempotent — a replayed POST /nodes/{id}/rotate takes a
       * second node out of its pools. A failed write is reported, not retried.
       */
      retry: false,
    },
  },
});
